/**
 * LiveFlight Vuex Store Module
 *
 * This store has been refactored into domain-specific modules for better maintainability:
 * - store/search.js    : Search parameters (airports, dates, travelers, cabin)
 * - store/results.js   : Flight results and suggestions
 * - store/filters.js   : Sorting and filtering options
 * - store/selection.js : Selected flight offer and baggage
 * - store/checkout.js  : Traveler details and contact information
 * - store/ui.js        : Loading states and search flow control
 *
 * The modules are aggregated here to maintain backward compatibility with existing components.
 * All components continue to access state via `$store.state.liveflight.*` without changes.
 */

import { vuex_subscribe_module, vuex_store_config } from "@/vuex";
import { load_state_from_local_storage } from "@/resources";
import {
  aggregatedState,
  aggregatedMutations,
  getLocalStorageKeys,
} from "./store/index";
import { sanitize_search_store } from "./store/search";

// Create the vuex config from aggregated state
const state_config = vuex_store_config(aggregatedState);

// Export serialized config for external use (e.g., state reset)
export const serialized_vuex_config_object = JSON.stringify(aggregatedState);

// Module configuration
const name = "liveflight";
const state = state_config.state;

// Load state from localStorage, then sanitize search keys
// (handles legacy flat keys migration + corrupted data normalization)
const loadedState = load_state_from_local_storage(name, state_config.state);
const sanitized = sanitize_search_store(loadedState || {});
Object.assign(loadedState, sanitized);

const module = {
  namespaced: true,
  state: loadedState,
  mutations: {
    // Auto-generated mutations from vuex_store_config
    ...state_config.mutations,
    // Custom mutations from domain modules (override auto-generated where needed)
    ...aggregatedMutations,
  },
  getters: state_config.getters,
  actions: state_config.actions,

  /**
   * Declares which keys should be persisted in local storage.
   * Keys are aggregated from all domain modules.
   */
  get_local_storage_submodule() {
    const keys = getLocalStorageKeys();
    const storageConfig = {};

    keys.forEach((key) => {
      storageConfig[key] = state_config.state[key];
    });

    return storageConfig;
  },
};

/**
 * Registers the liveflight module with the global Vuex store.
 * Called from main.js during brand initialization.
 *
 * @param {Object} prototype - Vue prototype to attach submodule config
 */
export function register_state_module(prototype) {
  if (prototype.$submodules == undefined) prototype.$submodules = {};
  prototype.$submodules[name] = {
    get_local_storage_submodule: module.get_local_storage_submodule(),
  };
  vuex_subscribe_module(name, state, module);
}
